import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { agentUsageQueryOptions, LIMIT_REACHED, type AgentUsage } from "./models";

/**
 * Today's AI use under the model list, as a percent of the daily limit (never an amount), when the
 * organization shows it.
 *
 * It shows what was known when the picker opened (ModelPill fetches it beforehand) and never
 * appears or changes while open: the menu and the sheet are placed by their height, so a late line
 * would move the rows under the pointer. Opening still asks again, for the next opening.
 */
export function UsageLine({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const [percent] = useState(
    () => queryClient.getQueryData<AgentUsage>(agentUsageQueryOptions.queryKey)?.percent
  );
  useQuery(agentUsageQueryOptions);
  if (percent == null) return null;
  const reached = percent >= 100;
  return (
    // As wide as the rows make the menu, never wider: its text wraps instead.
    <div className={cn("w-0 min-w-full space-y-1.5 border-t border-border/60 text-xs", className)}>
      <p className={reached ? "text-destructive" : "text-muted-foreground"}>
        {reached ? LIMIT_REACHED : `Today's AI use: ${percent}% of your daily limit`}
      </p>
      <div className="h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className={cn("h-full rounded-full", reached ? "bg-destructive" : "bg-primary")}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
}
